import HttpClientManager from '@/business/core/clients/http/helpers/manager.helper';
import type {
	IAdminLoginResponse,
	IAdminRecoveryCodeLoginPayload,
	IAdminTotpLoginPayload,
	IAdminTotpLoginResponse,
	IRegisterUserRequest,
	IUpdateUserInformation,
	IGetUserInformation,
	IVerifyOtpResponse,
	ISwitchContextRequest,
	ISwitchContextResponse,
	IAccountContext,
} from '@/business/modules/auth/auth.model';

class AuthRepository {
	httpClient: any;
	static #instance: AuthRepository;
	readonly ACCOUNT: string = 'Account';

	private constructor() {
		this.httpClient = HttpClientManager.getInstance();
	}

	public static getInstance(): AuthRepository {
		if (!AuthRepository.#instance) {
			AuthRepository.#instance = new AuthRepository();
		}

		return AuthRepository.#instance;
	}

	signup(data: IRegisterUserRequest) {
		return this.httpClient(`${this.ACCOUNT}/Register`, {
			method: 'post',
			data: { ...data },
			isShowNotify: true,
		});
	}

	signupForReservation(data: IRegisterUserRequest) {
		return this.httpClient(`${this.ACCOUNT}/Register`, {
			method: 'post',
			data: { ...data },
			isShowNotify: false,
		});
	}

	loginInit(phoneNumber: string) {
		return this.httpClient(`${this.ACCOUNT}/LoginInit`, {
			method: 'get',
			params: { PhoneNumber: phoneNumber },
			isShowNotify: true,
		});
	}

	loginInitForReservation(phoneNumber: string) {
		return this.httpClient(`${this.ACCOUNT}/LoginInit`, {
			method: 'get',
			params: { PhoneNumber: phoneNumber },
			isShowNotify: false,
		});
	}

	verifyOtp(otpCode: string): Promise<IVerifyOtpResponse> {
		return this.httpClient(`${this.ACCOUNT}/Login`, {
			method: 'get',
			params: { code: otpCode },
			isShowNotify: true,
		});
	}

	loginAsAdmin(otpCode: string): Promise<IAdminLoginResponse> {
		return this.httpClient(`${this.ACCOUNT}/Admin/Login`, {
			method: 'get',
			params: { code: otpCode },
			isShowNotify: true,
		});
	}

	verifyAdminTotpLogin(payload: IAdminTotpLoginPayload): Promise<IAdminTotpLoginResponse> {
		return this.httpClient(`${this.ACCOUNT}/Admin/Totp/Verify`, {
			method: 'post',
			data: payload,
			isShowNotify: true,
		});
	}

	verifyAdminRecoveryCodeLogin(payload: IAdminRecoveryCodeLoginPayload): Promise<IAdminTotpLoginResponse> {
		return this.httpClient(`${this.ACCOUNT}/Admin/Totp/RecoveryCode`, {
			method: 'post',
			data: payload,
			isShowNotify: true,
		});
	}

	updateUserInformation(userData: IUpdateUserInformation) {
		return this.httpClient(`${this.ACCOUNT}/UpdateUser`, {
			method: 'put',
			data: {
				...userData,
			},
			isShowNotify: true,
		});
	}

	loginByRefreshToken() {
		return this.httpClient(`${this.ACCOUNT}/LoginByRefreshToken`, {
			method: 'get',
			useRefreshTokenOnly: true,
		});
	}

	logout() {
		return this.httpClient(`${this.ACCOUNT}/Logout`, {
			method: 'post',
			isShowNotify: false,
		});
	}

	getUserInformation(): Promise<IGetUserInformation> {
		return this.httpClient(`${this.ACCOUNT}/GetInformation`, {
			method: 'get',
		});
	}

	getAvailableContexts(): Promise<IAccountContext[]> {
		return this.httpClient(`${this.ACCOUNT}/Contexts`, {
			method: 'get',
			isShowNotify: false,
		});
	}

	switchContext(data: ISwitchContextRequest): Promise<ISwitchContextResponse> {
		return this.httpClient(`${this.ACCOUNT}/switch-context`, {
			method: 'post',
			data: {
				targetContext: data.targetContext,
				businessId: data.businessId ?? undefined,
			},
			isShowNotify: false,
		});
	}
}

export default AuthRepository;
